/**
 * CostPerSignedCaseCalculator
 *
 * Rules (uses only the reader's own numbers; no defaults or benchmarks):
 * - Three channels: AI search, organic search, paid search. For each, the reader enters
 *   spend and signed cases for the same period.
 * - A channel is counted only when spend is entered. Blank or negative values are ignored.
 * - Cost per signed case = spend / signed cases, when signed cases > 0.
 * - Spend with 0 signed cases (or cases left blank) is reported as "no signed cases yet".
 * - The lowest cost per signed case among channels with cases is named; ties are reported.
 * - Signed cases must be whole numbers; decimals are rounded down.
 * - No tracking, storage, cookies or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";

const CHANNELS = [
  { key: "ai", label: "AI search (ChatGPT, Perplexity, Gemini, AI Overviews)" },
  { key: "organic", label: "Organic search" },
  { key: "paid", label: "Paid search" },
] as const;

type ChannelKey = (typeof CHANNELS)[number]["key"];

interface Row {
  spend: string;
  cases: string;
}

const parse = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const money = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const linkClass =
  "font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function CostPerSignedCaseCalculator() {
  const [rows, setRows] = useState<Record<ChannelKey, Row>>({
    ai: { spend: "", cases: "" },
    organic: { spend: "", cases: "" },
    paid: { spend: "", cases: "" },
  });

  const set = (key: ChannelKey, field: keyof Row, value: string) =>
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const results = CHANNELS.map((c) => {
    const spend = parse(rows[c.key].spend);
    const rawCases = parse(rows[c.key].cases);
    const cases = rawCases === null ? null : Math.floor(rawCases);
    const cost = spend !== null && cases !== null && cases > 0 ? spend / cases : null;
    return { ...c, spend, cases, cost };
  }).filter((r) => r.spend !== null);

  const withCost = results.filter((r) => r.cost !== null);
  const lowest = withCost.length ? Math.min(...withCost.map((r) => r.cost as number)) : null;
  const leaders = withCost.filter((r) => r.cost === lowest);
  const aiNoCases = results.some((r) => r.key === "ai" && r.cost === null);

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Cost per signed case calculator</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Enter what you spent on each channel and how many signed cases it produced over the same
        period, for example the last six months. Use any currency, as long as it is the same for
        every channel. Nothing is saved or sent anywhere.
      </p>

      <div className="mt-5 space-y-5">
        {CHANNELS.map((c) => (
          <fieldset key={c.key} className="min-w-0 rounded-lg border border-border p-4">
            <legend className="max-w-full px-1 text-sm font-semibold break-words">{c.label}</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor={`cpsc-${c.key}-spend`} className="block text-sm font-medium">
                  Spend for the period
                </label>
                <input
                  id={`cpsc-${c.key}-spend`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  placeholder="e.g. 12000"
                  value={rows[c.key].spend}
                  onChange={(e) => set(c.key, "spend", e.target.value)}
                  className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <div>
                <label htmlFor={`cpsc-${c.key}-cases`} className="block text-sm font-medium">
                  Signed cases from this channel
                </label>
                <input
                  id={`cpsc-${c.key}-cases`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  placeholder="e.g. 4"
                  value={rows[c.key].cases}
                  onChange={(e) => set(c.key, "cases", e.target.value)}
                  className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>
          </fieldset>
        ))}
      </div>

      <div aria-live="polite" className="mt-6 rounded-lg bg-muted p-4 text-sm">
        {results.length === 0 ? (
          <p className="text-muted-foreground">Enter spend for at least one channel to see results.</p>
        ) : (
          <>
            <ul className="space-y-2">
              {results.map((r) => (
                <li key={r.key}>
                  <span className="font-semibold">{r.label}: </span>
                  {r.cost !== null
                    ? `${money(r.cost)} per signed case (${money(r.spend as number)} across ${r.cases} case${r.cases === 1 ? "" : "s"}).`
                    : "no signed cases recorded yet, so this channel is still an experiment."}
                </li>
              ))}
            </ul>

            {leaders.length === 1 && withCost.length > 1 && (
              <p className="mt-4">
                {leaders[0].label} is currently your cheapest source of signed cases. Check it has
                capacity before moving budget away from it.
              </p>
            )}
            {leaders.length > 1 && (
              <p className="mt-4">
                {leaders.map((l) => l.label).join(" and ")} cost the same per signed case right now.
              </p>
            )}

            {aiNoCases && (
              <p className="mt-3">
                If AI search shows spend but no signed cases, first confirm your intake form records
                AI platforms as a lead source. Untagged leads can make a working channel look empty.{" "}
                <Link to="/services/$slug" params={{ slug: "ai-seo-geo" }} className={linkClass}>
                  See how GEO work is scoped and tracked
                </Link>
                .
              </p>
            )}

            <p className="mt-3">
              Need source-level tracking set up properly?{" "}
              <Link to="/services/$slug" params={{ slug: "law-firm-marketing-analytics" }} className={linkClass}>
                See analytics and call-tracking for law firms
              </Link>
              .
            </p>
          </>
        )}
      </div>
    </section>
  );
}
